<script setup lang="ts" generic="T">
    // @ts-nocheck

    /**
     * Återanvändbar bas för events som ska renderas i timeline. Du vill troligtvist inte använda den rakt av, utan
     * istället implementera en komponent som använder sig av denna. Se VardplatsTimelineEvent.vue
     */

    import { getEventColor } from "@/tabs/Operation/colors-utils";
    import { EventImpl } from "@fullcalendar/core/internal";
    import { computed, ref, toRefs, defineEmits } from "vue";
    import KCalendarOverlayPanel from "@/components/calendars/overlayPanels/KCalendarOverlayPanel.vue";

    const emit = defineEmits(["panelShow", "click"]);

    const props = withDefaults(
        defineProps<{
            event: EventImpl;
            colorKey?: string;
            data: T;
            /*
                Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
                key id som ska kollas mot och value index som ska användas vid hämtning av färg
                T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
            */
            colorKeyIndices?: { [key: string]: Map<string, number> };
            enablePanel?: boolean;
            customBackgroundColor?: string;
            customColor?: string;
            variant?: "default" | "sparradTid";
        }>(),
        {
            colorKey: "",
            enablePanel: true,
            variant: "default",
        }
    );

    const { event, colorKey, data, colorKeyIndices, enablePanel, customColor, customBackgroundColor } = toRefs(props);
    const panelRef = ref();
    const eventRef = ref();

    const color = computed(() => {
        // Om colorIndex är satt ska den overridea ev färg satt av aktuell colorKey
        if (typeof event?.value?.extendedProps?.colorIndex === "number") {
            return getEventColor(Number(event.value.extendedProps.colorIndex));
        }

        const colorProp = event?.value?.extendedProps?.[colorKey.value];
        const eventColorId = colorKeyIndices?.value?.[colorKey.value]?.get(colorProp) ?? colorProp;

        return getEventColor(Number(eventColorId ?? 0));
    });
    const backgroundColor = computed(() => customBackgroundColor?.value ?? color?.value?.backgroundColor);
    const textColor = computed(() => customColor?.value ?? color?.value?.textColor);
    const opacity = computed(() => {
        return event?.value?.extendedProps?.faded ? 0.5 : 1;
    });

    const onClick = (event: MouseEvent) => {
        /**
         * Stängd av panel funktion för att kunna anropa modal dialog istället
         * Visa <VardplatsSparraTid /> komponenten istället
         *
         */
        if (!enablePanel.value) {
            emit("click");

            return;
        }

        panelRef?.value?.toggle(event);
    };

    const onShow = () => {
        emit("panelShow");
    };
</script>

<template>
    <a :class="['timeline-event', 'k-event', `variant-${variant}`]" @click="onClick" ref="eventRef">
        <div class="background">
            <div v-if="variant !== 'sparradTid'" class="background-lightener" />
            <div v-if="variant !== 'sparradTid' && !event?.extendedProps?.faded" class="color-marker" />
            <div class="inner">
                <slot name="inner" :data="data" />
            </div>
        </div>
        <KCalendarOverlayPanel
            v-if="enablePanel"
            ref="panelRef"
            :data="data"
            :event="event"
            :colorKey="colorKey"
            @show="onShow"
        >
            <template #header>
                <slot name="panel-header" :panelRef="panelRef" />
            </template>
            <template #content>
                <slot name="panel-content" :panelRef="panelRef" />
            </template>
            <template #actions>
                <slot name="panel-actions" :panelRef="panelRef" />
            </template>
        </KCalendarOverlayPanel>
    </a>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    $color-marker-width: 0.5rem;

    .timeline-event {
        color: v-bind("textColor");
        border-radius: 4px;
        font-size: 0.8rem;
        white-space: nowrap;
        opacity: v-bind("opacity");

        &:hover {
            text-decoration: none;
            cursor: pointer;
        }

        .background {
            position: relative;
            border-radius: 4px;
            background-color: v-bind("backgroundColor");
            overflow: hidden;

            &:hover {
                min-width: fit-content;
                overflow: visible;
            }

            .background-lightener {
                position: absolute;
                z-index: 1;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                background-color: white;
                opacity: 0.6;
            }

            .color-marker {
                z-index: 2;
                width: $color-marker-width;
                background-color: v-bind("backgroundColor");
                height: 100%;
                position: absolute;
                left: 0;
                border-radius: 4px;
            }

            .inner {
                display: flex;
                gap: 0.4rem;
                position: relative;
                z-index: 2;
                padding: 0.3rem;
                padding-left: $color-marker-width + 0.3rem;
            }
        }

        &.variant-sparradTid {
            .background {
                background: repeating-linear-gradient(-45deg, #ffffff, #ffffff 1px, #e9ecec 1px, #e9ecec 7px);
                color: $color-text-dark;
            }
        }
    }

    ::v-deep .fc-event-dragging {
        border: 2px solid black;

        &:hover {
            all: unset;
        }
    }
</style>
